import { useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { ArrowRight, Building2, Layers, Mail, MapPin, Phone, Users, X, Bed, Clock, Globe } from 'lucide-react'
import { Link } from 'react-router-dom'
import { AMBITI_STATS } from '../data/ambitiStats'
import type { AmbitoId } from '../data/ambitiStats'
import type { Rsa } from '../data/rsa'
import type { StrutturaDisabilita } from '../data/struttureDisabilita'
import type { Cdi } from '../data/cdi'
import type { CasaDellaComunita } from '../data/CdC'
import type { CdCWithAmbito } from '../data/cdcAmbiti'
import { cdcSlug } from '../data/cdcAmbiti'
import type { StrutturaSanitaria } from '../data/struttureSanitarie'
import { strutturaAsstSlug } from '../data/struttureSanitarie'
import { formatEuro } from '../utils/dashboardFormatters'

type StrutturaType = 'RSA' | 'RSD' | 'CSS' | 'CDD' | 'CDI'

const TYPE_COLORS: Record<StrutturaType, string> = {
  RSA: '#f59e0b',
  RSD: '#8b5cf6',
  CSS: '#0ea5e9',
  CDD: '#10b981',
  CDI: '#10b981',
}

function fallback(value?: string | number | null) {
  if (value === null || value === undefined) return '—'
  const s = String(value).trim()
  return s.length > 0 ? s : '—'
}

function InfoRow({ label, value, icon: Icon }: { label: string; value: ReactNode; icon?: React.ComponentType<{ size?: number | string }> }) {
  return (
    <div style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
      <dt style={{ fontSize: 12, color: 'var(--text-3)', minWidth: 120, fontWeight: 600 }}>{label}</dt>
      <dd style={{ fontSize: 12, color: 'var(--text-1)', margin: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
        {Icon && <Icon size={13} />}
        {value}
      </dd>
    </div>
  )
}

// ── RSA Detail ──────────────────────────────────────────────────────────────

function RsaDetailContent({ rsa, detailUrl }: { rsa: Rsa; detailUrl: string }) {
  const ambito = AMBITI_STATS.find(a => a.id === rsa.ambitoId)
  const color = TYPE_COLORS.RSA

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999, color, background: `${color}20`, border: `1px solid ${color}40` }}>
          <Building2 size={12} /> RSA
        </span>
        {rsa.nucleoAlzheimer && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999, color: '#8b5cf6', background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.3)' }}>
            Nucleo Alzheimer
          </span>
        )}
        {ambito && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 999, color: 'var(--accent-blue)', background: 'hsla(210,100%,56%,0.08)', border: '1px solid hsla(210,100%,56%,0.2)' }}>
            <Layers size={12} /> Ambito di {ambito.nome}
          </span>
        )}
      </div>

      <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-0)', margin: 0, letterSpacing: '-0.01em' }}>{rsa.nome}</h1>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
        <StatBox label="Lista d'attesa" value={rsa.listaAttesaOrdinaria} sub="Ordinaria" color={color} />
        {rsa.nucleoAlzheimer && <StatBox label="Lista d'attesa" value={rsa.listaAttesaAlzheimer} sub="Alzheimer" color="#8b5cf6" />}
        <StatBox label="Retta" value={`${rsa.rettaMin} – ${rsa.rettaMax}`} sub="Ordinaria" color={color} />
        {rsa.nucleoAlzheimer && rsa.rettaMinAlzheimer && <StatBox label="Retta" value={`${rsa.rettaMinAlzheimer} – ${rsa.rettaMaxAlzheimer}`} sub="Alzheimer" color="#8b5cf6" />}
        {(rsa.totalePostiOrdinaria ?? 0) > 0 && <StatBox label="Posti totali" value={rsa.totalePostiOrdinaria!} sub="Ordinaria" color={color} />}
        {(rsa.postiDisponibiliOrdinaria ?? 0) > 0 && <StatBox label="Posti disponibili" value={rsa.postiDisponibiliOrdinaria!} color="#10b981" />}
      </div>

      <dl style={{ margin: 0 }}>
        <InfoRow label="Località" value={fallback(rsa.localita)} icon={MapPin} />
        <InfoRow label="Indirizzo" value={fallback(rsa.indirizzo)} icon={MapPin} />
        <InfoRow label="Provincia" value={fallback(rsa.provincia)} />
        {rsa.telefono && <InfoRow label="Telefono" value={rsa.telefono} icon={Phone} />}
        {rsa.email && <InfoRow label="Email" value={<a href={`mailto:${rsa.email}`} style={{ color: 'var(--accent-blue)', textDecoration: 'none' }}>{rsa.email}</a>} icon={Mail} />}
        {rsa.sito && <InfoRow label="Sito web" value={<a href={rsa.sito} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-blue)', textDecoration: 'none', wordBreak: 'break-all' }}>{rsa.sito.replace(/^https?:\/\//, '').replace(/\/$/, '')}</a>} icon={Globe} />}
        <InfoRow label="Aggiornamento" value={fallback(rsa.dataAggiornamento)} icon={Clock} />
      </dl>
    </div>
  )
}

// ── Disabilità Detail (RSD/CSS/CDD) ────────────────────────────────────────

function DisabilitaDetailContent({ struttura, tipo }: { struttura: StrutturaDisabilita; tipo: StrutturaType }) {
  const ambito = AMBITI_STATS.find(a => a.id === struttura.ambitoId)
  const color = TYPE_COLORS[tipo]

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999, color, background: `${color}20`, border: `1px solid ${color}40` }}>
          <Building2 size={12} /> {tipo}
        </span>
        {struttura.listaAttesa && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999, color: '#ef4444', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)' }}>
            <Users size={12} /> Lista d'attesa
          </span>
        )}
        {ambito && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 999, color: 'var(--accent-blue)', background: 'hsla(210,100%,56%,0.08)', border: '1px solid hsla(210,100%,56%,0.2)' }}>
            <Layers size={12} /> Ambito di {ambito.nome}
          </span>
        )}
      </div>

      <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-0)', margin: 0, letterSpacing: '-0.01em' }}>{struttura.nome}</h1>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        <StatBox label="Posti a contratto" value={struttura.postiContratto} color={color} />
        <StatBox label="Posti disponibili" value={struttura.postiDisponibili} color="#10b981" />
        <StatBox label="Persone in lista" value={struttura.personeInLista} color="#ef4444" />
      </div>

      <dl style={{ margin: 0 }}>
        <InfoRow label="Comune" value={fallback(struttura.comune)} icon={MapPin} />
        <InfoRow label="Indirizzo" value={fallback(struttura.indirizzo)} icon={MapPin} />
        <InfoRow label="Telefono" value={fallback(struttura.telefono)} icon={Phone} />
        <InfoRow label="Email" value={fallback(struttura.email)} icon={Mail} />
      </dl>
    </div>
  )
}

// ── Stat Box ────────────────────────────────────────────────────────────────

function StatBox({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color: string }) {
  return (
    <div style={{
      padding: '10px 14px',
      borderRadius: 10,
      background: `${color}08`,
      border: `1px solid ${color}25`,
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

// ── Dialog Wrapper ──────────────────────────────────────────────────────────

export function StrutturaDetailDialog({
  open,
  onClose,
  detailUrl,
  fromUrl,
  children,
}: {
  open: boolean
  onClose: () => void
  detailUrl: string
  fromUrl: string
  children: ReactNode
}) {
  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10020,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        animation: 'fadeIn 0.15s ease-out',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 'min(680px, 94vw)',
          minHeight: '60vh',
          maxHeight: '86vh',
          overflowY: 'auto',
          background: 'var(--tooltip-bg)',
          border: '1px solid var(--tooltip-border)',
          borderRadius: 14,
          color: 'var(--tooltip-text)',
          boxShadow: '0 14px 42px rgba(0,0,0,0.35)',
          padding: 20,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ color: 'var(--text-0)', fontSize: 14, fontWeight: 700 }}>Dettaglio struttura</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Link
              to={detailUrl}
              state={{ from: fromUrl }}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                color: 'var(--text-2)', fontSize: 12, fontWeight: 600, textDecoration: 'none',
              }}
            >
              Apri pagina <ArrowRight style={{ width: 14, height: 14 }} />
            </Link>
            <button
              type="button"
              onClick={onClose}
              aria-label="Chiudi"
              style={{
                width: 28, height: 28, borderRadius: 8,
                border: '1px solid var(--border-md)', background: 'var(--bg-2)',
                color: 'var(--text-2)', display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              }}
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {children}
      </div>
    </div>,
    document.body,
  )
}

// ── Convenience Dialogs ─────────────────────────────────────────────────────

export function RsaDialog({ rsa, onClose, fromUrl }: { rsa: Rsa | null; onClose: () => void; fromUrl: string }) {
  if (!rsa) return null
  const slug = rsa.id
  return (
    <StrutturaDetailDialog open detailUrl={`/rsa/${slug}`} fromUrl={fromUrl} onClose={onClose}>
      <RsaDetailContent rsa={rsa} detailUrl={`/rsa/${slug}`} />
    </StrutturaDetailDialog>
  )
}

export function DisabilitaDialog({
  struttura,
  tipo,
  onClose,
  fromUrl,
}: {
  struttura: StrutturaDisabilita | null
  tipo: StrutturaType
  onClose: () => void
  fromUrl: string
}) {
  if (!struttura) return null
  const slug = struttura.id
  const prefix = tipo.toLowerCase()
  return (
    <StrutturaDetailDialog open detailUrl={`/${prefix}/${slug}`} fromUrl={fromUrl} onClose={onClose}>
      <DisabilitaDetailContent struttura={struttura} tipo={tipo} />
    </StrutturaDetailDialog>
  )
}

// ── CDI Detail ──────────────────────────────────────────────────────────────

function CdiDetailContent({ cdi }: { cdi: Cdi }) {
  const ambito = AMBITI_STATS.find(a => a.id === cdi.ambitoId)
  const color = TYPE_COLORS.CDI

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999, color, background: `${color}20`, border: `1px solid ${color}40` }}>
          <Building2 size={12} /> CDI
        </span>
        {ambito && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 999, color: 'var(--accent-blue)', background: 'hsla(210,100%,56%,0.08)', border: '1px solid hsla(210,100%,56%,0.2)' }}>
            <Layers size={12} /> Ambito di {ambito.nome}
          </span>
        )}
      </div>

      <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-0)', margin: 0, letterSpacing: '-0.01em' }}>{cdi.nome}</h1>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        <StatBox label="Posti accreditati" value={cdi.postiAccreditati} color={color} />
        <StatBox label="Lista att. full-time" value={cdi.listaAttesaFullTime} color="#ef4444" />
        <StatBox label="Lista att. part-time" value={cdi.listaAttesaPartTime} color="#f59e0b" />
      </div>

      <dl style={{ margin: 0 }}>
        <InfoRow label="Località" value={fallback(cdi.localita)} icon={MapPin} />
        <InfoRow label="Indirizzo" value={fallback(cdi.indirizzo)} icon={MapPin} />
        <InfoRow label="Aggiornamento" value={fallback(cdi.dataAggiornamento)} icon={Clock} />
      </dl>
    </div>
  )
}

export function CdiDialog({ cdi, onClose, fromUrl }: { cdi: Cdi | null; onClose: () => void; fromUrl: string }) {
  if (!cdi) return null
  return (
    <StrutturaDetailDialog open detailUrl={`/cdi/${cdi.id}`} fromUrl={fromUrl} onClose={onClose}>
      <CdiDetailContent cdi={cdi} />
    </StrutturaDetailDialog>
  )
}

// ── CdC Detail ──────────────────────────────────────────────────────────────

function CdcDetailContent({ entry }: { entry: CdCWithAmbito }) {
  const casa = entry.casa
  const ambito = AMBITI_STATS.find(a => a.id === entry.ambitoId)
  const color = '#0ea5e9'
  const totalServizi = casa.sezioni.reduce((s, sz) => s + sz.servizi.length, 0)

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999, color, background: `${color}20`, border: `1px solid ${color}40` }}>
          <Building2 size={12} /> Casa della Comunità
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999, color: casa.attiva === 1 ? '#16a34a' : '#dc2626', background: casa.attiva === 1 ? 'rgba(22,163,106,0.12)' : 'rgba(220,38,38,0.12)', border: `1px solid ${casa.attiva === 1 ? '#16a34a40' : '#dc262640'}` }}>
          {casa.attiva === 1 ? 'Aperta' : 'Chiusa'}
        </span>
        {ambito && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 999, color: 'var(--accent-blue)', background: 'hsla(210,100%,56%,0.08)', border: '1px solid hsla(210,100%,56%,0.2)' }}>
            <Layers size={12} /> Ambito di {ambito.nome}
          </span>
        )}
      </div>

      <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-0)', margin: 0, letterSpacing: '-0.01em' }}>{casa.nome}</h1>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        <StatBox label="Servizi" value={totalServizi} color={color} />
        <StatBox label="Sezioni" value={casa.sezioni.length} color="#8b5cf6" />
        {entry.pnrrInvestimento > 0 && <StatBox label="Investimento PNRR" value={formatEuro(entry.pnrrInvestimento)} color="#f59e0b" />}
      </div>

      <dl style={{ margin: 0 }}>
        <InfoRow label="Indirizzo" value={fallback(casa.indirizzo)} icon={MapPin} />
        <InfoRow label="Città" value={fallback(casa.citta)} icon={MapPin} />
        {casa.sezioneTerritoriale && <InfoRow label="Distretto" value={casa.sezioneTerritoriale} />}
        {casa.sezioneDistrettuale && <InfoRow label="ASST" value={casa.sezioneDistrettuale} />}
        {casa.sitoWeb && <InfoRow label="Sito web" value={<a href={`https://${casa.sitoWeb}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-blue)', textDecoration: 'none', wordBreak: 'break-all' }}>{casa.sitoWeb}</a>} icon={Globe} />}
      </dl>

      {casa.sezioni.length > 0 && (
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>Servizi ({totalServizi})</div>
          {casa.sezioni.map(sezione => (
            <div key={sezione.id}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)', marginBottom: 4 }}>{sezione.titolo}</div>
              <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-2)', fontSize: 12 }}>
                {sezione.servizi.map(servizio => (
                  <li key={servizio.id} style={{ marginBottom: 2 }}>{servizio.nome}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function CdcDialog({ entry, onClose, fromUrl }: { entry: CdCWithAmbito | null; onClose: () => void; fromUrl: string }) {
  if (!entry) return null
  return (
    <StrutturaDetailDialog open detailUrl={`/casadellacomunita/${cdcSlug(entry.casa)}`} fromUrl={fromUrl} onClose={onClose}>
      <CdcDetailContent entry={entry} />
    </StrutturaDetailDialog>
  )
}

// ── Struttura Sanitaria (ASST) Detail ───────────────────────────────────────

function StrutturaSanitariaDetailContent({ struttura }: { struttura: StrutturaSanitaria }) {
  const ambito = AMBITI_STATS.find(a => a.id === struttura.ambitoId)
  const color = '#0891b2'

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999, color, background: `${color}20`, border: `1px solid ${color}40` }}>
          <Building2 size={12} /> {struttura.tipo}
        </span>
        {struttura.bsdName && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999, color: '#8b5cf6', background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.3)' }}>
            {struttura.bsdName}
          </span>
        )}
        {ambito && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 999, color: 'var(--accent-blue)', background: 'hsla(210,100%,56%,0.08)', border: '1px solid hsla(210,100%,56%,0.2)' }}>
            <Layers size={12} /> Ambito di {ambito.nome}
          </span>
        )}
      </div>

      <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-0)', margin: 0, letterSpacing: '-0.01em' }}>
        {struttura.bsdDenominazione || struttura.nome}
      </h1>

      <dl style={{ margin: 0 }}>
        <InfoRow label="Tipologia" value={fallback(struttura.tipo)} icon={Building2} />
        <InfoRow label="Comune" value={fallback(struttura.comune)} icon={MapPin} />
        <InfoRow label="Distretto" value={fallback(struttura.distretto)} />
        <InfoRow label="CAP" value={fallback(struttura.cap)} />
        <InfoRow label="Indirizzo" value={fallback(struttura.indirizzo)} icon={MapPin} />
        {struttura.telefono && (
          <InfoRow
            label="Telefono"
            value={<a href={`tel:${struttura.telefono}`} style={{ color: 'var(--accent-blue)', textDecoration: 'none' }}>{struttura.telefono}</a>}
            icon={Phone}
          />
        )}
        {struttura.email && (
          <InfoRow
            label="Email"
            value={<a href={`mailto:${struttura.email}`} style={{ color: 'var(--accent-blue)', textDecoration: 'none' }}>{struttura.email}</a>}
            icon={Mail}
          />
        )}
        {struttura.orari && <InfoRow label="Orari" value={struttura.orari} icon={Clock} />}
        {struttura.url && (
          <InfoRow
            label="Sito web"
            value={<a href={struttura.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-blue)', textDecoration: 'none', wordBreak: 'break-all' }}>{struttura.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}</a>}
            icon={Globe}
          />
        )}
        {struttura.altreInfo && <InfoRow label="Altre info" value={struttura.altreInfo} />}
      </dl>

      {struttura.struttureWeb.length > 0 && (
        <div style={{ display: 'grid', gap: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>Strutture web collegate</div>
          <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-2)', fontSize: 12 }}>
            {struttura.struttureWeb.map((item, idx) => (
              <li key={`${struttura.id}-web-${idx}`} style={{ marginBottom: 2 }}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export function StrutturaSanitariaDialog({
  struttura,
  onClose,
  fromUrl,
}: {
  struttura: StrutturaSanitaria | null
  onClose: () => void
  fromUrl: string
}) {
  if (!struttura) return null
  const slug = strutturaAsstSlug(struttura.nome)
  return (
    <StrutturaDetailDialog open detailUrl={`/struttureasst/${slug}`} fromUrl={fromUrl} onClose={onClose}>
      <StrutturaSanitariaDetailContent struttura={struttura} />
    </StrutturaDetailDialog>
  )
}

// ── Exported detail content for pages ───────────────────────────────────────
export { RsaDetailContent, DisabilitaDetailContent, CdiDetailContent, CdcDetailContent, StrutturaSanitariaDetailContent }
